import { Children, type FC, Fragment, type ReactElement, type ReactNode } from "react";

import { css, cx } from "@emotion/css";
import { type CellProps, Colors, Fonts, Tag } from "@snapchat/snap-design-system";

import { GroupTags } from "../../../groups/components/groups-table/types";
import { Flexbox } from "../../../core/components";
import { CopyToClipboardButton } from "../CopyToClipboardButton";
import { type TagType, TagWithTooltip } from "../TagWithTooltip";
import { DecreaseText } from "../text";

interface TwoLineCellProps extends Omit<CellProps, "children"> {
    text: string;
    subText: string;
    tags?: TagType<GroupTags>[];
    children?: ReactNode;
    testId?: string;
}

/**
 * Renders two lines of text, tags and a copy button if applicable.
 * Check out "Name / ID" cell in this table as an example:
 * https://www.figma.com/file/mCjj1rpJnQIWLc8O94DflE/Lens-Scheduler-2.0?node-id=32%3A5522
 */
export const TwoLineCell: FC<TwoLineCellProps> = ({ text, subText, tags, children }) => {
    tags = tags ?? [];

    const childrenArray = Children.toArray(children as ReactElement[]);
    const copyButton = childrenArray[0] as ReactElement;

    if ((copyButton && copyButton?.type !== CopyToClipboardButton) || childrenArray?.length > 1) {
        throw new Error("CopyToClipboardButton is the only child component expected");
    }

    return (
        <Flexbox flexDirection="column" className={wrapper}>
            <Flexbox alignItems="baseline">
                <DecreaseText className={nameCss} title={text} data-testid="two-line-cell-text">
                    {text}
                </DecreaseText>
                {tags.map((tag, index) => (
                    <Fragment key={tag.text}>
                        {tag.text === GroupTags.READ_ONLY ? (
                            <TagWithTooltip
                                tooltipText="These lenses are read-only and are being managed elsewhere"
                                tag={tag}
                                className={cx(tagCss, {
                                    [defaultTagCss]: !tag.status,
                                    [leftOffsetCss]: !!text || index > 0,
                                })}
                            />
                        ) : (
                            <Tag
                                size="small"
                                status={tag?.status}
                                className={cx(tagCss, {
                                    [defaultTagCss]: !tag.status,
                                    [leftOffsetCss]: !!text || index > 0,
                                })}
                            >
                                {tag.text}
                            </Tag>
                        )}
                    </Fragment>
                ))}
            </Flexbox>
            <Flexbox alignItems="baseline">
                <DecreaseText className={idCss} title={subText} data-testid="two-line-cell-sub-text">
                    {subText}
                </DecreaseText>
                {copyButton}
            </Flexbox>
        </Flexbox>
    );
};

const wrapper = css`
    ${Fonts.Paragraph.P300};
    width: 100%;

    & > div:first-child {
        margin-bottom: 4px;
    }

    .sds-base-button.ant-btn {
        line-height: 16px;
    }
`;

const nameCss = css`
    line-height: 16px;
    color: ${Colors.Black.V100};
`;

const idCss = css`
    line-height: 16px;
    color: ${Colors.Gray.V300};
`;

const tagCss = css`
    padding: 2px 8px;

    font-size: 11px;
    font-weight: 400;
    white-space: nowrap;

    border: none;
`;

const leftOffsetCss = css`
    margin-left: 8px;
`;

const defaultTagCss = css`
    color: ${Colors.Black.V100};
    background-color: ${Colors.Gray.V150};
`;
